<template>
  <div class="accounts-page">
    <!-- 净资产概览卡片 -->
    <div class="net-worth-card">
      <div class="card-header">
        <h3>账户</h3>
        <div class="actions">
          <el-button circle size="small" @click="showAddAccountDialog">
            <el-icon><Plus /></el-icon>
          </el-button>
          <el-button circle size="small" @click="showSettingsDialog">
            <el-icon><Setting /></el-icon>
          </el-button>
        </div>
      </div>
      
      <div class="net-worth-content">
        <div class="main-amount">
          <span class="amount-sign">{{ netWorth >= 0 ? '' : '-' }}</span>
          <span class="amount-number">{{ formatAmount(Math.abs(netWorth)) }}</span>
          <el-button link size="small" @click="toggleAmountVisibility">
            <el-icon><View v-if="showAmount" /><Hide v-else /></el-icon>
          </el-button>
        </div>
        
        <div class="sub-amounts">
          <span class="sub-amount">总资产 {{ formatAmount(totalAssets) }}</span>
          <span class="sub-amount">总负债 {{ formatAmount(totalLiabilities) }}</span>
        </div>
      </div>
    </div>

    <!-- 我的账户 -->
    <div class="accounts-section">
      <div class="section-header">
        <h4>我的账户</h4>
        <div class="section-actions">
          <template v-if="sortMode">
            <el-button type="primary" size="small" @click="saveOrder" :loading="savingOrder">保存排序</el-button>
            <el-button size="small" @click="exitSortMode">取消</el-button>
          </template>
          <template v-else>
            <el-button text size="small" @click="toggleAccountsExpanded">
              <el-icon><Operation /></el-icon>
            </el-button>
            <el-button text size="small" @click="enterSortMode">
              <el-icon><Edit /></el-icon>
              排序
            </el-button>
          </template>
        </div>
      </div>

      <!-- 动态渲染所有账户类型 -->
      <div 
        v-for="accountType in sortedAccountTypes" 
        :key="accountType"
        class="account-category"
      >
        <div class="category-header" @click="toggleCategory(accountType)">
          <div class="category-info">
            <div class="category-icon" :class="getTypeConfig(accountType).iconClass">
              <el-icon>
                <component :is="getTypeConfig(accountType).icon" />
              </el-icon>
            </div>
            <div class="category-details">
              <span class="category-name">{{ accountType }}</span>
              <span class="account-count">({{ getAccountsByType(accountType).length }})</span>
            </div>
          </div>
          <div class="category-amount">
            <span class="amount">{{ formatAmount(getCategoryTotal(accountType)) }}</span>
            <el-icon class="expand-icon" :class="{ expanded: expandedCategories[accountType] }">
              <ArrowRight />
            </el-icon>
          </div>
        </div>
        
        <div class="account-list" v-show="expandedCategories[accountType] || sortMode">
          <!-- 排序模式：保留分类结构，组内支持拖拽 -->
          <template v-if="sortMode">
            <!-- 多子分类：按子分类分组 -->
            <template v-if="getSubcategories(accountType).length > 1">
              <div v-for="subcat in getSubcategories(accountType)" :key="subcat">
                <div class="subcategory-header" style="pointer-events:none;">
                  <span class="subcategory-name">{{ subcat || '未分类' }}</span>
                  <span class="subcategory-amount">{{ formatAmount(getSubcategoryTotal(accountType, subcat)) }}</span>
                </div>
                <div
                  v-for="(account, index) in getSortableBySubcat(accountType, subcat)"
                  :key="account.id"
                  class="account-item sortable-item"
                  draggable="true"
                  @dragstart="onDragStart(accountType + '::' + (subcat || ''), index, $event)"
                  @dragover.prevent="onDragOver(accountType + '::' + (subcat || ''), index)"
                  @drop.prevent="onDrop(accountType + '::' + (subcat || ''), index)"
                  @dragend="onDragEnd"
                  :class="{ 'drag-over': dragOverType === (accountType + '::' + (subcat || '')) && dragOverIndex === index }"
                >
                  <div class="drag-handle"><el-icon><Operation /></el-icon></div>
                  <div class="account-info">
                    <div class="account-icon"><el-icon><component :is="getTypeConfig(accountType).icon" /></el-icon></div>
                    <div class="account-details">
                      <span class="account-name">{{ account.account_name }}</span>
                    </div>
                  </div>
                  <span class="account-balance">{{ formatAmount(account.balance) }}</span>
                </div>
              </div>
            </template>
            <!-- 单子分类：平铺 -->
            <template v-else>
              <div
                v-for="(account, index) in getSortableAccounts(accountType)"
                :key="account.id"
                class="account-item sortable-item"
                draggable="true"
                @dragstart="onDragStart(accountType, index, $event)"
                @dragover.prevent="onDragOver(accountType, index)"
                @drop.prevent="onDrop(accountType, index)"
                @dragend="onDragEnd"
                :class="{ 'drag-over': dragOverType === accountType && dragOverIndex === index }"
              >
                <div class="drag-handle"><el-icon><Operation /></el-icon></div>
                <div class="account-info">
                  <div class="account-icon"><el-icon><component :is="getTypeConfig(accountType).icon" /></el-icon></div>
                  <div class="account-details">
                    <span class="account-name">{{ account.account_name }}</span>
                    <span class="account-type" v-if="account.account_subcategory">{{ account.account_subcategory }}</span>
                  </div>
                </div>
                <span class="account-balance">{{ formatAmount(account.balance) }}</span>
              </div>
            </template>
          </template>

          <!-- 多子分类：显示分组标题 -->
          <template v-else-if="getSubcategories(accountType).length > 1">
            <div
              v-for="subcat in getSubcategories(accountType)"
              :key="subcat"
            >
              <div class="subcategory-header" @click.stop="toggleSubcategory(accountType, subcat)">
                <span class="subcategory-name">{{ subcat || '未分类' }}</span>
                <div class="subcategory-right">
                  <span class="subcategory-amount">{{ formatAmount(getSubcategoryTotal(accountType, subcat)) }}</span>
                  <el-icon class="expand-icon" :class="{ expanded: isSubcategoryExpanded(accountType, subcat) }">
                    <ArrowRight />
                  </el-icon>
                </div>
              </div>
              <div v-show="isSubcategoryExpanded(accountType, subcat)">
                <div
                  v-for="account in getAccountsByTypeAndSubcat(accountType, subcat)"
                  :key="account.id"
                  class="account-item account-item-indented"
                  @click="viewAccountDetail(account)"
                >
                  <div class="account-info">
                    <div class="account-icon">
                      <el-icon><component :is="getTypeConfig(accountType).icon" /></el-icon>
                    </div>
                    <div class="account-details">
                      <span class="account-name">{{ account.account_name }}</span>
                      <span v-if="account.exclude_from_net_worth" class="exclude-net-worth-tag">不计净资产</span>
                    </div>
                  </div>
                  <div class="account-item-right">
                    <span class="account-balance" :class="{ negative: account.balance < 0 }">
                      {{ formatAmount(account.balance) }}
                    </span>
                    <el-button class="quick-hide-btn" size="small" circle title="隐藏账户" @click.stop="quickHideAccount(account)">
                      <el-icon><Hide /></el-icon>
                    </el-button>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- 单子分类 / 无子分类：平铺显示 -->
          <template v-else-if="!sortMode">
            <div
              v-for="account in getAccountsByType(accountType)"
              :key="account.id"
              class="account-item"
              @click="viewAccountDetail(account)"
            >
              <div class="account-info">
                <div class="account-icon">
                  <el-icon>
                    <component :is="getTypeConfig(accountType).icon" />
                  </el-icon>
                </div>
                <div class="account-details">
                  <span class="account-name">{{ account.account_name }}</span>
                  <span class="account-type" v-if="account.account_subcategory">
                    {{ account.account_subcategory }}
                  </span>
                  <span v-if="account.exclude_from_net_worth" class="exclude-net-worth-tag">不计净资产</span>
                </div>
              </div>
              <div class="account-item-right">
                <span class="account-balance" :class="{ negative: account.balance < 0 }">
                  {{ formatAmount(account.balance) }}
                </span>
                <el-button class="quick-hide-btn" size="small" circle title="隐藏账户" @click.stop="quickHideAccount(account)">
                  <el-icon><Hide /></el-icon>
                </el-button>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- 隐藏账户 -->
      <div class="account-category hidden-category" v-if="hiddenAccounts.length > 0">
        <div class="category-header" @click="toggleCategory('hidden')">
          <div class="category-info">
            <div class="category-icon hidden-icon">
              <el-icon><Hide /></el-icon>
            </div>
            <div class="category-details">
              <span class="category-name">隐藏账户</span>
              <span class="account-count">({{ hiddenAccounts.length }})</span>
            </div>
          </div>
          <div class="category-amount">
            <span class="amount">{{ formatAmount(getHiddenTotal()) }}</span>
            <el-icon class="expand-icon" :class="{ expanded: expandedCategories.hidden }">
              <ArrowRight />
            </el-icon>
          </div>
        </div>
        
        <div class="account-list" v-show="expandedCategories.hidden">
          <!-- 按一级账户类型分组 -->
          <div v-for="accountType in sortedHiddenTypes" :key="accountType">
            <!-- 多类型时显示类型标题行 -->
            <div
              v-if="sortedHiddenTypes.length > 1"
              class="hidden-type-header"
              @click.stop="toggleCategory('hidden::' + accountType)"
            >
              <div class="hidden-type-info">
                <div class="category-icon" :class="getTypeConfig(accountType).iconClass" style="width:24px;height:24px;font-size:12px;border-radius:6px;">
                  <el-icon><component :is="getTypeConfig(accountType).icon" /></el-icon>
                </div>
                <span class="hidden-type-name">{{ accountType }}</span>
                <span class="account-count">({{ getHiddenByType(accountType).length }})</span>
              </div>
              <div class="hidden-type-right">
                <span class="subcategory-amount">{{ formatAmount(getHiddenTypeTotal(accountType)) }}</span>
                <el-icon class="expand-icon" :class="{ expanded: expandedCategories['hidden::' + accountType] !== false }">
                  <ArrowRight />
                </el-icon>
              </div>
            </div>

            <div v-show="expandedCategories['hidden::' + accountType] !== false">
              <!-- 多子分类时按子分类分组 -->
              <template v-if="getHiddenSubcategories(accountType).length > 1">
                <div v-for="subcat in getHiddenSubcategories(accountType)" :key="subcat">
                  <div class="subcategory-header" @click.stop="toggleSubcategory('hidden::' + accountType, subcat)">
                    <span class="subcategory-name">{{ subcat || '未分类' }}</span>
                    <div class="subcategory-right">
                      <span class="subcategory-amount">{{ formatAmount(getHiddenSubcatTotal(accountType, subcat)) }}</span>
                      <el-icon class="expand-icon" :class="{ expanded: isSubcategoryExpanded('hidden::' + accountType, subcat) }">
                        <ArrowRight />
                      </el-icon>
                    </div>
                  </div>
                  <div v-show="isSubcategoryExpanded('hidden::' + accountType, subcat)">
                    <div
                      v-for="account in getHiddenByTypeAndSubcat(accountType, subcat)"
                      :key="account.id"
                      class="account-item hidden-account-item account-item-indented"
                    >
                      <div class="account-info">
                        <div class="account-icon"><el-icon><Hide /></el-icon></div>
                        <div class="account-details">
                          <span class="account-name">{{ account.account_name }}</span>
                        </div>
                      </div>
                      <div class="account-actions">
                        <span class="account-balance">{{ formatAmount(account.balance) }}</span>
                        <el-button type="primary" size="small" @click.stop="restoreAccount(account)">恢复显示</el-button>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <!-- 单子分类平铺 -->
              <template v-else>
                <div
                  v-for="account in getHiddenByType(accountType)"
                  :key="account.id"
                  class="account-item hidden-account-item"
                >
                  <div class="account-info">
                    <div class="account-icon"><el-icon><Hide /></el-icon></div>
                    <div class="account-details">
                      <span class="account-name">{{ account.account_name }}</span>
                      <span class="account-type" v-if="account.account_subcategory">{{ account.account_subcategory }}</span>
                    </div>
                  </div>
                  <div class="account-actions">
                    <span class="account-balance">{{ formatAmount(account.balance) }}</span>
                    <el-button type="primary" size="small" @click.stop="restoreAccount(account)">恢复显示</el-button>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 账户余额查询对话框 -->
    <el-dialog v-model="balanceDialogVisible" title="账户余额查询" width="90%" :close-on-click-modal="false">
      <div v-if="selectedAccount">
        <el-form :model="balanceQuery" label-width="80px">
          <el-form-item label="账户">
            <el-input :value="selectedAccount.account_name" readonly />
          </el-form-item>
          <el-form-item label="查询日期">
            <el-date-picker
              v-model="balanceQuery.as_of_date"
              type="date"
              placeholder="选择日期，不选则查询最新余额"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              style="width: 100%"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="queryBalance" :loading="balanceLoading">
              查询
            </el-button>
          </el-form-item>
        </el-form>
        
        <!-- 余额结果 -->
        <div v-if="balanceResult" class="balance-result">
          <el-divider>查询结果</el-divider>
          <div class="result-item">
            <span class="label">账户名称：</span>
            <span>{{ balanceResult.account_name }}</span>
          </div>
          <div class="result-item">
            <span class="label">余额：</span>
            <span class="balance-value">¥{{ formatAmount(balanceResult.balance) }}</span>
          </div>
          <div class="result-item">
            <span class="label">截止日期：</span>
            <span>{{ balanceResult.as_of_date }}</span>
          </div>
          <div v-if="balanceResult.last_transaction_date" class="result-item">
            <span class="label">最后交易时间：</span>
            <span>{{ formatDateTime(balanceResult.last_transaction_date) }}</span>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 账户交易明细对话框 -->
    <el-dialog v-model="transactionsDialogVisible" title="账户交易明细" width="95%" top="5vh">
      <div v-if="selectedAccount">
        <!-- 查询条件 -->
        <el-form :model="transactionQuery" inline style="margin-bottom: 20px;">
          <el-form-item label="日期范围">
            <el-date-picker
              v-model="transactionDateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              style="width: 240px"
            />
          </el-form-item>
          <el-form-item label="交易类型">
            <el-select 
              v-model="transactionQuery.transaction_type" 
              placeholder="选择类型" 
              clearable
              style="width: 120px"
            >
              <el-option label="支出" value="支出" />
              <el-option label="收入" value="收入" />
              <el-option label="转账" value="转账" />
              <el-option label="借入" value="借入" />
              <el-option label="借出" value="借出" />
              <el-option label="收款" value="收款" />
              <el-option label="还款" value="还款" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="queryTransactions">查询</el-button>
            <el-button @click="resetTransactionQuery">重置</el-button>
          </el-form-item>
        </el-form>
        
        <!-- 交易记录表格 -->
        <el-table 
          v-loading="transactionsLoading"
          :data="accountTransactions" 
          stripe 
          border
          height="400"
        >
          <el-table-column prop="transaction_date" label="交易时间" width="160">
            <template #default="{ row }">
              {{ formatDateTime(row.transaction_date) }}
            </template>
          </el-table-column>
          <el-table-column prop="transaction_type" label="类型" width="80">
            <template #default="{ row }">
              <el-tag :type="getTransactionTypeTag(row.transaction_type)" size="small">
                {{ row.transaction_type }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="account_change_amount" label="账户变动" width="120" align="right">
            <template #default="{ row }">
              <span :class="row.account_change_amount >= 0 ? 'amount-positive' : 'amount-negative'">
                {{ row.account_change_amount >= 0 ? '+' : '' }}¥{{ formatAmount(Math.abs(row.account_change_amount)) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="amount" label="交易金额" width="120" align="right">
            <template #default="{ row }">
              ¥{{ formatAmount(row.amount) }}
            </template>
          </el-table-column>
          <el-table-column label="对方账户" width="150">
            <template #default="{ row }">
              <span v-if="row.primary_account_id !== selectedAccount.id">
                {{ row.primary_account_name }}
              </span>
              <span v-else-if="row.secondary_account_name">
                {{ row.secondary_account_name }}
              </span>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="description" label="备注" min-width="200" show-overflow-tooltip />
          
          <!-- 操作列 -->
          <el-table-column label="操作" width="136" align="center" fixed="right">
            <template #default="{ row }">
              <el-button
                type="primary"
                size="small"
                class="transaction-action-btn"
                @click="editTransaction(row)"
                plain
                circle
                title="编辑交易记录"
              >
                <el-icon><Edit /></el-icon>
              </el-button>
              <el-button 
                type="danger" 
                size="small" 
                class="transaction-action-btn"
                @click="deleteTransaction(row)"
                :loading="deletingTransactionId === row.id"
                plain
                circle
                title="删除交易记录"
              >
                <el-icon><CircleClose /></el-icon>
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        
        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            :current-page="transactionQuery.page"
            :page-size="transactionQuery.page_size"
            :page-sizes="[10, 20, 50]"
            :total="transactionTotal"
            layout="total, sizes, prev, pager, next"
            @size-change="handleTransactionSizeChange"
            @current-change="handleTransactionCurrentChange"
          />
        </div>
      </div>
    </el-dialog>

    <!-- 编辑账户交易记录 -->
    <TransactionDialog
      v-model="editTransactionDialogVisible"
      :transaction="editingTransaction"
      :is-edit="true"
      @saved="handleTransactionSaved"
    />

    <!-- 账户详情弹窗 -->
    <el-dialog 
      v-model="accountDetailDialogVisible" 
      :title="selectedAccount ? selectedAccount.account_name : '账户详情'" 
      width="90%" 
      top="5vh"
      :close-on-click-modal="false"
    >
      <div v-if="selectedAccount" v-loading="monthlyStatsLoading">
        <div class="account-overview-container">
          <!-- 账户概览卡片 -->
          <div class="account-overview-card" :class="parseFloat(selectedAccount.balance) >= 0 ? 'positive-balance' : 'negative-balance'">
            <div class="account-header">
              <div class="account-basic-info">
                <h3 class="account-title">{{ selectedAccount.account_name }}</h3>
                <span class="account-type-tag">{{ selectedAccount.account_type }}</span>
              </div>
              <div class="balance-section">
                <div class="balance-label">人民币余额</div>
                <div class="summary-value main-balance" :class="parseFloat(selectedAccount.balance) >= 0 ? 'positive' : 'negative'">
                  {{ formatAmount(selectedAccount.balance) }}
                  <el-icon class="settings-icon" @click="showAccountSettings"><Setting /></el-icon>
                </div>
                <div class="summary-flow">
                  <span>总流入 {{ formatAmount(totalFlowIn) }}</span>
                  <span>总流出 {{ formatAmount(totalFlowOut) }}</span>
                </div>
                <div class="account-actions-bar">
                  <el-button type="primary" @click="addTransactionForAccount">
                    <el-icon><Plus /></el-icon>
                    记一笔
                  </el-button>
                </div>
              </div>
            </div>
          </div>

          <!-- 时间轴容器 -->
          <div class="timeline-section">
            <div class="section-header">
              <h4>账户明细</h4>
              <div class="load-controls">
                <el-button 
                  @click="loadMoreMonthlyData" 
                  :loading="loadingMore" 
                  size="small" 
                  type="primary" 
                  plain
                  :disabled="!hasMoreData"
                >
                  <el-icon v-if="!loadingMore"><Download /></el-icon>
                  {{ loadingMore ? '加载中...' : '加载更多' }}
                </el-button>
                <span class="load-tip">按月显示，点击查看详情</span>
              </div>
            </div>
            
            <div 
              class="timeline-container" 
              ref="timelineContainer" 
              @scroll="handleScrollThrottled"
              v-loading="monthlyStatsLoading"
              element-loading-text="加载中..."
            >
              <div class="timeline-content">
                <div 
                  v-for="monthData in sortedMonthlyData" 
                  :key="`${monthData.year}-${monthData.month}`"
                  class="timeline-item"
                  @click="viewMonthDetails(monthData)"
                >
                  <div class="timeline-date">
                    <div class="year">{{ monthData.year }}</div>
                    <div class="month">{{ monthData.month_name }}</div>
                  </div>
                  
                  <div class="timeline-content-item">
                    <div class="month-summary">
                      <div class="flow-summary">
                        <div class="flow-item flow-in">
                          <span class="label">流入</span>
                          <span class="amount">{{ formatAmount(monthData.flow_in) }}</span>
                        </div>
                        <div class="flow-item flow-out">
                          <span class="label">流出</span>
                          <span class="amount">{{ formatAmount(monthData.flow_out) }}</span>
                        </div>
                      </div>
                      
                      <div class="balance-info">
                        <div class="balance-amount" :class="monthData.balance >= 0 ? 'positive' : 'negative'">
                          {{ formatAmount(monthData.balance) }}
                        </div>
                        <div class="balance-label">余额</div>
                      </div>
                      
                      <div class="transaction-info">
                        <span class="transaction-count">{{ monthData.transaction_count }}笔</span>
                        <el-icon class="arrow-icon"><ArrowRight /></el-icon>
                      </div>
                    </div>
                    
                    <!-- 如果该月有交易，显示类型分布 -->
                    <div v-if="monthData.transaction_count > 0" class="transaction-types">
                      <div 
                        v-for="(typeData, typeName) in monthData.by_type" 
                        :key="typeName"
                        class="type-tag"
                      >
                        {{ typeName }} ¥{{ formatAmount(typeData.amount) }}
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- 加载更多指示器 -->
                <div v-if="hasMoreData && allMonthlyData.length > 0" class="load-more-indicator">
                  <div class="auto-load-tip">
                    <el-icon><ArrowDown /></el-icon>
                    <span>继续滚动加载更多</span>
                  </div>
                  <div v-if="loadingMore" class="loading-indicator">
                    <el-icon class="loading"><Loading /></el-icon>
                    <span>正在加载历史数据...</span>
                  </div>
                </div>
                
                <!-- 没有更多数据 -->
                <div v-if="!hasMoreData && allMonthlyData.length > 0" class="no-more-data">
                  <el-icon><Check /></el-icon>
                  已显示所有历史数据
                </div>
                
                <!-- 没有任何数据 -->
                <div v-if="!monthlyStatsLoading && allMonthlyData.length === 0" class="no-data">
                  <div class="no-data-content">
                    <el-icon class="no-data-icon"><DocumentCopy /></el-icon>
                    <p>该账户暂无交易记录</p>
                    <span class="no-data-tip">请检查其他年份或添加新的交易记录</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 账户设置弹窗 -->
    <el-dialog
      v-model="accountSettingsDialogVisible"
      :title="`设置 - ${selectedAccount ? selectedAccount.account_name : ''}`"
      width="min(500px, 92vw)"
      :close-on-click-modal="false"
      class="account-settings-dialog"
    >
      <div v-if="selectedAccount">
        <el-form label-width="100px">
          <el-form-item label="账户名称">
            <el-input 
              v-model="accountSettings.accountName"
              placeholder="请输入账户名称"
            />
          </el-form-item>
          
          <el-form-item label="账户类型">
            <el-select 
              v-model="accountSettings.accountType" 
              placeholder="选择账户类型" 
              style="width: 100%"
              @change="onAccountTypeChange"
            >
              <el-option 
                v-for="type in accountTypes" 
                :key="type.value" 
                :label="type.label" 
                :value="type.value" 
              />
            </el-select>
            <div class="form-tip">修改账户类型会影响账户的分类显示</div>
          </el-form-item>
          
          <el-form-item label="二级分类" v-if="currentEditSubcategories.length > 0">
            <el-select 
              v-model="accountSettings.accountSubcategory" 
              placeholder="选择二级分类" 
              clearable 
              style="width: 100%"
            >
              <el-option 
                v-for="sub in currentEditSubcategories" 
                :key="sub.subcategory_name" 
                :label="sub.subcategory_name" 
                :value="sub.subcategory_name" 
              />
            </el-select>
          </el-form-item>
          
          <el-form-item label="所属银行" v-if="accountSettings.accountType === '银行卡'">
            <el-select 
              v-model="accountSettings.bankId" 
              placeholder="选择银行" 
              clearable 
              filterable 
              style="width: 100%"
            >
              <el-option 
                v-for="bank in banks" 
                :key="bank.id" 
                :label="bank.bank_name" 
                :value="bank.id" 
              />
            </el-select>
          </el-form-item>
          
          <el-form-item label="卡片类型" v-if="accountSettings.accountType === '银行卡'">
            <el-input 
              v-model="accountSettings.cardType"
              placeholder="如：储蓄卡、信用卡"
            />
          </el-form-item>
          
          <el-form-item label="所有者">
            <el-select 
              v-model="accountSettings.ownerType" 
              placeholder="选择所有者" 
              style="width: 100%"
            >
              <el-option 
                v-for="owner in ownerTypes" 
                :key="owner.value" 
                :label="owner.label" 
                :value="owner.value" 
              />
            </el-select>
          </el-form-item>
          
          <el-form-item label="显示账户">
            <el-switch
              v-model="accountSettings.isVisible"
              active-text="显示"
              inactive-text="隐藏"
            />
            <div class="form-tip">关闭后在前端界面不再显示，可在「隐藏账户」中恢复</div>
          </el-form-item>

          <el-form-item label="计入净资产">
            <el-switch
              v-model="accountSettings.includeInNetWorth"
              active-text="计入"
              inactive-text="不计入"
            />
            <div class="form-tip">关闭后该账户余额不计入顶部净资产统计</div>
          </el-form-item>

          <el-form-item label="备注">
            <el-input 
              v-model="accountSettings.description"
              type="textarea"
              :rows="3"
              placeholder="为此账户添加备注信息（200字以内）"
              maxlength="200"
              show-word-limit
            />
          </el-form-item>
        </el-form>
      </div>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="accountSettingsDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="saveAccountSettings" :loading="savingSettings">
            保存设置
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 记一笔弹窗 -->
    <el-dialog 
      v-model="addTransactionDialogVisible" 
      title="记一笔"
      width="450px"
      :close-on-click-modal="false"
      class="add-transaction-dialog"
    >
      <div class="quick-transaction-form" v-if="selectedAccount">
        <!-- 交易类型选择 -->
        <div class="type-tabs">
          <div 
            v-for="tab in quickTransactionTabs" 
            :key="tab.value"
            class="type-tab"
            :class="{ active: quickTransaction.type === tab.value }"
            @click="quickTransaction.type = tab.value"
          >
            {{ tab.label }}
          </div>
        </div>

        <!-- 金额输入 -->
        <div class="amount-input-section">
          <span class="currency-symbol">¥</span>
          <el-input 
            v-model="quickTransaction.amount" 
            placeholder="0.00"
            type="number"
            class="amount-input"
          />
        </div>

        <!-- 分类选择 -->
        <div class="form-field" v-if="quickTransaction.type === 'expense' || quickTransaction.type === 'income'">
          <label>分类</label>
          <el-cascader
            v-model="quickTransaction.category"
            :options="quickTransactionCategories"
            :props="{ expandTrigger: 'hover', value: 'id', label: 'category_name', children: 'children' }"
            placeholder="选择分类"
            clearable
            filterable
            style="width: 100%"
          />
        </div>

        <!-- 转入账户（转账/借出/收款时显示） -->
        <div class="form-field" v-if="['transfer', 'borrow_out', 'repay'].includes(quickTransaction.type)">
          <label>转入账户</label>
          <AccountSelect
            v-model="quickTransaction.toAccountId"
            placeholder="选择转入账户"
            style="width: 100%"
            :accounts="accounts.filter(a => a.id !== selectedAccount?.id)"
          />
        </div>

        <!-- 时间 -->
        <div class="form-field">
          <label>时间</label>
          <el-date-picker
            v-model="quickTransaction.date"
            type="datetime"
            placeholder="选择时间"
            format="YYYY-MM-DD HH:mm"
            value-format="YYYY-MM-DD HH:mm:ss"
            style="width: 100%"
          />
        </div>

        <!-- 备注 -->
        <div class="form-field">
          <label>备注</label>
          <el-input v-model="quickTransaction.description" placeholder="输入备注..." />
        </div>
      </div>
      
      <template #footer>
        <el-button @click="addTransactionDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveQuickTransaction" :loading="savingTransaction">
          保存
        </el-button>
      </template>
    </el-dialog>

    <!-- 添加账户弹窗 -->
    <el-dialog 
      v-model="addAccountDialogVisible" 
      title="添加账户"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form :model="newAccount" label-width="100px">
        <el-form-item label="账户名称" required>
          <el-input v-model="newAccount.account_name" placeholder="请输入账户名称" />
        </el-form-item>
        
        <el-form-item label="账户类型" required>
          <el-select v-model="newAccount.account_type" placeholder="选择账户类型" style="width: 100%">
            <el-option 
              v-for="type in accountTypes" 
              :key="type.value" 
              :label="type.label" 
              :value="type.value" 
            />
          </el-select>
        </el-form-item>
        
        <el-form-item label="二级分类" v-if="currentSubcategories.length > 0">
          <el-select v-model="newAccount.account_subcategory" placeholder="选择二级分类" clearable style="width: 100%">
            <el-option 
              v-for="sub in currentSubcategories" 
              :key="sub.subcategory_name" 
              :label="sub.subcategory_name" 
              :value="sub.subcategory_name" 
            />
          </el-select>
        </el-form-item>
        
        <el-form-item label="所属银行" v-if="newAccount.account_type === '银行卡'">
          <el-select v-model="newAccount.bank_id" placeholder="选择银行" clearable filterable style="width: 100%">
            <el-option 
              v-for="bank in banks" 
              :key="bank.id" 
              :label="bank.bank_name" 
              :value="bank.id" 
            />
          </el-select>
        </el-form-item>
        
        <el-form-item label="卡片类型" v-if="newAccount.account_type === '银行卡'">
          <el-input v-model="newAccount.card_type" placeholder="如：储蓄卡、信用卡" />
        </el-form-item>
        
        <el-form-item label="初始余额">
          <el-input-number 
            v-model="newAccount.balance" 
            :precision="2" 
            :step="100" 
            style="width: 100%"
          />
        </el-form-item>
        
        <el-form-item label="所有者">
          <el-select v-model="newAccount.owner_type" placeholder="选择所有者" style="width: 100%">
            <el-option 
              v-for="owner in ownerTypes" 
              :key="owner.value" 
              :label="owner.label" 
              :value="owner.value" 
            />
          </el-select>
        </el-form-item>
        
        <el-form-item label="备注">
          <el-input 
            v-model="newAccount.description" 
            type="textarea" 
            :rows="3" 
            placeholder="为此账户添加备注信息"
            maxlength="200"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      
      <template #footer>
        <el-button @click="addAccountDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveNewAccount" :loading="savingAccount">
          保存
        </el-button>
      </template>
    </el-dialog>

    <!-- 全局设置弹窗 -->
    <el-dialog 
      v-model="globalSettingsDialogVisible" 
      title="账户设置"
      width="600px"
      :close-on-click-modal="false"
    >
      <div class="global-settings-content">
        <el-tabs>
          <el-tab-pane label="显示设置">
            <div class="settings-section">
              <h4>账户显示</h4>
              <p class="settings-tip">可以在账户详情中单独设置每个账户的显示/隐藏状态</p>
            </div>
          </el-tab-pane>
          
          <el-tab-pane label="分类管理">
            <div class="settings-section">
              <h4>收支分类</h4>
              <p class="settings-tip">管理收入和支出的分类</p>
              <el-button type="primary" @click="router.push('/categories')">
                管理收支分类
              </el-button>
              
              <el-divider />
              
              <h4>账户分类</h4>
              <p class="settings-tip">系统支持6种账户类型：现金、银行卡、电子钱包、投资账户、借贷账户、其他</p>
              <p class="settings-tip">每种类型可以设置二级分类，在添加账户时选择</p>
              <el-button type="primary" @click="router.push('/categories')">
                管理账户分类
              </el-button>
            </div>
          </el-tab-pane>
          
          <el-tab-pane label="关于">
            <div class="settings-section">
              <h4>个人财务管理系统</h4>
              <p>版本：1.0.0</p>
              <p>功能：账户管理、交易记录、分类管理、数据统计</p>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
      
      <template #footer>
        <el-button type="primary" @click="globalSettingsDialogVisible = false">
          关闭
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { 
  Plus, Setting, View, Hide, Operation, Edit, Money, CreditCard, Iphone, 
  TrendCharts, More, DocumentCopy, ArrowRight, Wallet, Download, ArrowDown, Loading, Check, CircleClose
} from '@element-plus/icons-vue'
import api from '../api'
import dayjs from 'dayjs'
import { ElMessage, ElMessageBox } from 'element-plus'
import TransactionDialog from '../components/TransactionDialog.vue'
import AccountSelect from '../components/AccountSelect.vue'

export default {
  name: 'Accounts',
  components: {
    Plus, Setting, View, Hide, Operation, Edit, Money, CreditCard, Iphone,
    TrendCharts, More, DocumentCopy, ArrowRight, Wallet, Download, ArrowDown, Loading, Check, CircleClose,
    TransactionDialog, AccountSelect
  },
  setup () {
    const store = useStore()
    const router = useRouter()
    
    // 响应式数据
    const showAmount = ref(true)
    const expandedCategories = reactive({
      cash: true,
      bankCard: true,
      eWallet: true,
      investment: true,
      other: true,
      hidden: false
    })
    const balanceDialogVisible = ref(false)
    const transactionsDialogVisible = ref(false)
    const accountDetailDialogVisible = ref(false)
    const accountSettingsDialogVisible = ref(false)
    const selectedAccount = ref(null)
    const balanceLoading = ref(false)
    const transactionsLoading = ref(false)
    const monthlyStatsLoading = ref(false)
    const loadingMore = ref(false)
    const savingSettings = ref(false)
    const balanceResult = ref(null)
    const accountTransactions = ref([])
    const allMonthlyData = ref([])
    const transactionTotal = ref(0)
    const transactionDateRange = ref([])
    const currentYear = ref(new Date().getFullYear())
    const currentMonth = ref(new Date().getMonth() + 1)
    const hasMoreData = ref(true)
    const timelineContainer = ref(null)
    const deletingTransactionId = ref(null)
    const editTransactionDialogVisible = ref(false)
    const editingTransaction = ref(null)

    const accountSettings = reactive({
      accountName: '',
      accountType: '',
      accountSubcategory: '',
      bankId: null,
      cardType: '',
      ownerType: '',
      isVisible: true,
      includeInNetWorth: true,
      description: ''
    })

    // 获取当前编辑账户类型的二级分类选项
    const currentEditSubcategories = computed(() => {
      return accountCategoryConfig.value[accountSettings.accountType] || []
    })

    // 账户类型改变时，清空二级分类
    const onAccountTypeChange = () => {
      accountSettings.accountSubcategory = ''
    }

    // 快速记账相关
    const addTransactionDialogVisible = ref(false)
    const savingTransaction = ref(false)
    const quickTransactionTabs = [
      { label: '支出', value: 'expense' },
      { label: '收入', value: 'income' },
      { label: '转账', value: 'transfer' },
      { label: '借出', value: 'borrow_out' },
      { label: '还款', value: 'repay' }
    ]
    const quickTransaction = reactive({
      type: 'expense',
      amount: '',
      category: [],
      toAccountId: null,
      date: null,
      description: ''
    })
    const expenseCategories = ref([])
    const incomeCategories = ref([])
    
    const balanceQuery = reactive({
      as_of_date: null
    })
    
    const transactionQuery = reactive({
      start_date: null,
      end_date: null,
      transaction_type: null,
      page: 1,
      page_size: 20
    })

    // 计算属性
    const accounts = computed(() => store.state.accounts)
    
    // 动态获取所有账户类型及其账户列表
    const accountsByType = computed(() => {
      const typeMap = {}
      accounts.value.forEach(account => {
        const type = account.account_type
        if (!typeMap[type]) {
          typeMap[type] = []
        }
        typeMap[type].push(account)
      })
      return typeMap
    })
    
    // 定义账户类型的显示配置（图标、颜色等）
    const accountTypeConfig = {
      '现金': {
        icon: 'Money',
        iconClass: 'cash-icon',
        order: 1
      },
      '银行卡': {
        icon: 'CreditCard',
        iconClass: 'bank-card-icon',
        order: 2
      },
      '电子钱包': {
        icon: 'Iphone',
        iconClass: 'ewallet-icon',
        order: 3
      },
      '投资账户': {
        icon: 'TrendCharts',
        iconClass: 'investment-icon',
        order: 4
      },
      '借贷账户': {
        icon: 'DocumentCopy',
        iconClass: 'loan-icon',
        order: 5
      },
      '其他': {
        icon: 'More',
        iconClass: 'other-icon',
        order: 99
      }
    }
    
    // 获取排序后的账户类型列表
    const sortedAccountTypes = computed(() => {
      return Object.keys(accountsByType.value).sort((a, b) => {
        const orderA = accountTypeConfig[a]?.order || 999
        const orderB = accountTypeConfig[b]?.order || 999
        return orderA - orderB
      })
    })
    
    // 获取某个类型的账户列表
    const getAccountsByType = (type) => {
      return accountsByType.value[type] || []
    }

    // 获取某个账户类型下的所有二级分类（去重排序）
    const getSubcategories = (type) => {
      const list = accountsByType.value[type] || []
      const set = new Set(list.map(a => a.account_subcategory || ''))
      return [...set].sort((a, b) => {
        if (!a) return 1  // 未分类排最后
        if (!b) return -1
        return a.localeCompare(b, 'zh')
      })
    }

    // 获取某个类型+子分类下的账户列表
    const getAccountsByTypeAndSubcat = (type, subcat) => {
      return (accountsByType.value[type] || []).filter(
        a => (a.account_subcategory || '') === subcat
      )
    }

    // 获取某个子分类的余额合计
    const getSubcategoryTotal = (type, subcat) => {
      return getAccountsByTypeAndSubcat(type, subcat)
        .reduce((sum, a) => sum + parseFloat(a.balance || 0), 0)
    }

    // 子分类展开状态（默认展开）
    const expandedSubcategories = reactive({})

    const subcategoryKey = (type, subcat) => `${type}::${subcat}`

    const isSubcategoryExpanded = (type, subcat) => {
      const key = subcategoryKey(type, subcat)
      return expandedSubcategories[key] !== false
    }

    const toggleSubcategory = (type, subcat) => {
      const key = subcategoryKey(type, subcat)
      expandedSubcategories[key] = !isSubcategoryExpanded(type, subcat)
    }
    
    // 获取某个类型的图标配置
    const getTypeConfig = (type) => {
      return accountTypeConfig[type] || {
        icon: 'More',
        iconClass: 'other-icon',
        order: 999
      }
    }
    
    // 为了向后兼容，保留原有的计算属性
    const cashAccounts = computed(() => 
      accounts.value.filter(account => account.account_type === '现金')
    )
    
    const bankCardAccounts = computed(() => 
      accounts.value.filter(account => account.account_type === '银行卡')
    )
    
    const eWalletAccounts = computed(() => 
      accounts.value.filter(account => account.account_type === '电子钱包')
    )
    
    const investmentAccounts = computed(() => 
      accounts.value.filter(account => account.account_type === '投资账户')
    )
    
    const loanAccounts = computed(() => 
      accounts.value.filter(account => account.account_type === '借贷账户')
    )
    
    const otherAccounts = computed(() => 
      accounts.value.filter(account => account.account_type === '其他')
    )

    // 隐藏账户列表
    const hiddenAccounts = ref([])

    const totalAssets = computed(() => {
      return [...accounts.value, ...hiddenAccounts.value]
        .filter(account => account.balance >= 0 && !account.exclude_from_net_worth)
        .reduce((sum, account) => sum + parseFloat(account.balance), 0)
    })

    const totalLiabilities = computed(() => {
      return Math.abs(
        [...accounts.value, ...hiddenAccounts.value]
          .filter(account => account.balance < 0 && !account.exclude_from_net_worth)
          .reduce((sum, account) => sum + parseFloat(account.balance), 0)
      )
    })

    const netWorth = computed(() => totalAssets.value - totalLiabilities.value)

    // 按时间倒序排列的月度数据（从近到远）
    const sortedMonthlyData = computed(() => {
      return allMonthlyData.value
        .sort((a, b) => {
          if (a.year !== b.year) {
            return b.year - a.year // 年份倒序
          }
          return b.month - a.month // 月份倒序
        })
    })

    // 计算总流入和总流出
    const totalFlowIn = computed(() => {
      return allMonthlyData.value.reduce((sum, month) => sum + (month.flow_in || 0), 0)
    })

    const totalFlowOut = computed(() => {
      return allMonthlyData.value.reduce((sum, month) => sum + (month.flow_out || 0), 0)
    })

    // 方法
    const toggleAmountVisibility = () => {
      showAmount.value = !showAmount.value
    }

    const toggleAccountsExpanded = () => {
      const allExpanded = Object.values(expandedCategories).every(v => v)
      Object.keys(expandedCategories).forEach(key => {
        expandedCategories[key] = !allExpanded
      })
    }

    const toggleCategory = (category) => {
      expandedCategories[category] = !expandedCategories[category]
    }

    const getCategoryTotal = (category) => {
      // 如果是特定的已知类型，使用原有逻辑
      let categoryAccounts = []
      switch (category) {
        case 'cash':
          categoryAccounts = cashAccounts.value
          break
        case 'bankCard':
          categoryAccounts = bankCardAccounts.value
          break
        case 'eWallet':
          categoryAccounts = eWalletAccounts.value
          break
        case 'investment':
          categoryAccounts = investmentAccounts.value
          break
        case 'loan':
          categoryAccounts = loanAccounts.value
          break
        case 'other':
          categoryAccounts = otherAccounts.value
          break
        default:
          // 如果是动态类型，直接从accountsByType获取
          categoryAccounts = accountsByType.value[category] || []
      }
      return categoryAccounts.reduce((sum, account) => sum + parseFloat(account.balance), 0)
    }

    const viewAccountDetail = async (account) => {
      selectedAccount.value = account
      accountDetailDialogVisible.value = true
      
      // 加载账户月度统计数据
      await loadAccountMonthlyStats()
    }

    const queryBalance = async () => {
      try {
        balanceLoading.value = true
        const params = {}
        if (balanceQuery.as_of_date) {
          params.as_of_date = balanceQuery.as_of_date
        }
        const response = await api.getAccountBalance(selectedAccount.value.id, params)
        balanceResult.value = response
      } catch (error) {
        console.error('查询余额失败:', error)
      } finally {
        balanceLoading.value = false
      }
    }

    const loadAccountMonthlyStats = async () => {
      try {
        monthlyStatsLoading.value = true
        allMonthlyData.value = []
        hasMoreData.value = true
        currentYear.value = new Date().getFullYear()

        // 从当前年份开始向前查找，自动跳过空年份
        await loadMoreMonthlyData()

        // 数据不足6个月时，再多加载一批
        if (allMonthlyData.value.length < 6 && hasMoreData.value) {
          await loadMoreMonthlyData()
        }
      } catch (error) {
        console.error('加载账户月度统计失败:', error)
      } finally {
        monthlyStatsLoading.value = false
      }
    }

    const loadMoreMonthlyData = async () => {
      if (loadingMore.value || !hasMoreData.value) return

      try {
        loadingMore.value = true

        // 逐年向前查找，自动跳过空年份，直到找到有数据的年份或到达边界
        while (currentYear.value >= 2000) {
          const year = currentYear.value
          currentYear.value--

          const response = await api.getAccountMonthlyStats(selectedAccount.value.id, { year })

          if (response && response.monthly_stats) {
            const monthsWithTransactions = response.monthly_stats
              .filter(month => month.transaction_count > 0)
              .map(month => ({ ...month, year }))

            if (monthsWithTransactions.length > 0) {
              allMonthlyData.value.push(...monthsWithTransactions)
              break // 找到数据，本次加载完成
            }
          }
        }

        if (currentYear.value < 2000) {
          hasMoreData.value = false
        }
      } catch (error) {
        console.error('加载更多月度数据失败:', error)
      } finally {
        loadingMore.value = false
      }
    }

    const handleScroll = (event) => {
      const { scrollTop, scrollHeight, clientHeight } = event.target
      
      // 当滚动到底部附近时，加载更多数据
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        loadMoreMonthlyData()
      }
    }

    // 防抖的滚动处理
    let scrollTimer = null
    const handleScrollThrottled = (event) => {
      if (scrollTimer) return
      
      scrollTimer = setTimeout(() => {
        const { scrollTop, scrollHeight, clientHeight } = event.target
        
        // 当滚动到底部附近时，加载更多数据
        if (scrollTop + clientHeight >= scrollHeight - 100 && hasMoreData.value && !loadingMore.value) {
          loadMoreMonthlyData()
        }
        
        scrollTimer = null
      }, 200) // 200ms防抖
    }

    const viewMonthDetails = (monthData) => {
      // 查看某月的详细交易记录
      const startDate = `${monthData.year}-${monthData.month.toString().padStart(2, '0')}-01`
      let endDate
      if (monthData.month === 12) {
        endDate = `${monthData.year + 1}-01-01`
      } else {
        endDate = `${monthData.year}-${(monthData.month + 1).toString().padStart(2, '0')}-01`
      }
      
      // 设置交易查询参数并打开交易明细弹窗
      Object.assign(transactionQuery, {
        start_date: startDate,
        end_date: endDate,
        transaction_type: null,
        page: 1,
        page_size: 20
      })
      transactionDateRange.value = [startDate, endDate]
      accountDetailDialogVisible.value = false
      transactionsDialogVisible.value = true
      queryTransactions()
    }

    const viewTransactions = (account) => {
      selectedAccount.value = account
      accountTransactions.value = []
      transactionTotal.value = 0
      transactionDateRange.value = []
      Object.assign(transactionQuery, {
        start_date: null,
        end_date: null,
        transaction_type: null,
        page: 1,
        page_size: 20
      })
      transactionsDialogVisible.value = true
      queryTransactions()
    }

    const queryTransactions = async () => {
      try {
        transactionsLoading.value = true
        const params = { ...transactionQuery }
        if (transactionDateRange.value && transactionDateRange.value.length === 2) {
          params.start_date = transactionDateRange.value[0]
          params.end_date = transactionDateRange.value[1]
        }
        const response = await api.getAccountTransactions(selectedAccount.value.id, params)
        accountTransactions.value = response.items
        transactionTotal.value = response.total
      } catch (error) {
        console.error('查询交易明细失败:', error)
      } finally {
        transactionsLoading.value = false
      }
    }

    const deleteTransaction = async (transaction) => {
      try {
        // 确认删除
        await ElMessageBox.confirm(
          `确定要删除这笔交易记录吗？\n\n时间：${formatDateTime(transaction.transaction_date)}\n类型：${transaction.transaction_type}\n金额：¥${formatAmount(transaction.amount)}`,
          '删除确认',
          {
            confirmButtonText: '确定删除',
            cancelButtonText: '取消',
            type: 'warning',
            dangerouslyUseHTMLString: false
          }
        )
        
        deletingTransactionId.value = transaction.id
        await api.deleteTransaction(transaction.id)
        ElMessage.success('交易记录删除成功')
        queryTransactions() // 刷新列表
        
        // 刷新账户列表和月度统计
        loadAllAccounts()
        if (accountDetailDialogVisible.value) {
          loadAccountMonthlyStats()
        }
      } catch (error) {
        if (error === 'cancel') {
          return // 用户取消删除
        }
        console.error('删除交易记录失败:', error)
        ElMessage.error('删除失败，请重试')
      } finally {
        deletingTransactionId.value = null
      }
    }

    const editTransaction = (transaction) => {
      editingTransaction.value = { ...transaction }
      editTransactionDialogVisible.value = true
    }

    const handleTransactionSaved = async () => {
      editTransactionDialogVisible.value = false
      editingTransaction.value = null
      await queryTransactions()
      await loadAllAccounts()
      if (accountDetailDialogVisible.value) {
        await loadAccountMonthlyStats()
      }
    }

    const showAccountSettings = async () => {
      if (!selectedAccount.value) return
      
      // 加载配置数据（如果还没加载）
      if (Object.keys(accountCategoryConfig.value).length === 0) {
        try {
          const [categoryConfig, bankList] = await Promise.all([
            api.getAccountCategoryConfig(),
            api.getBanks()
          ])
          accountCategoryConfig.value = categoryConfig
          banks.value = bankList
        } catch (error) {
          console.error('加载配置失败:', error)
        }
      }
      
      // 初始化设置数据
      accountSettings.accountName = selectedAccount.value.account_name
      accountSettings.accountType = selectedAccount.value.account_type
      accountSettings.accountSubcategory = selectedAccount.value.account_subcategory || ''
      accountSettings.bankId = selectedAccount.value.bank_id
      accountSettings.cardType = selectedAccount.value.card_type || ''
      accountSettings.ownerType = selectedAccount.value.owner_type || '本人'
      accountSettings.isVisible = selectedAccount.value.is_active !== false
      accountSettings.includeInNetWorth = !selectedAccount.value.exclude_from_net_worth
      accountSettings.description = selectedAccount.value.description || ''
      
      accountSettingsDialogVisible.value = true
    }

    const saveAccountSettings = async () => {
      try {
        savingSettings.value = true
        
        // 构建更新数据
        const updateData = {}
        
        // 检查哪些字段被修改了
        if (accountSettings.accountName !== selectedAccount.value.account_name) {
          updateData.account_name = accountSettings.accountName
        }
        
        if (accountSettings.accountType !== selectedAccount.value.account_type) {
          updateData.account_type = accountSettings.accountType
        }
        
        if (accountSettings.accountSubcategory !== (selectedAccount.value.account_subcategory || '')) {
          updateData.account_subcategory = accountSettings.accountSubcategory || null
        }
        
        if (accountSettings.bankId !== selectedAccount.value.bank_id) {
          updateData.bank_id = accountSettings.bankId
        }
        
        if (accountSettings.cardType !== (selectedAccount.value.card_type || '')) {
          updateData.card_type = accountSettings.cardType || null
        }
        
        if (accountSettings.ownerType !== (selectedAccount.value.owner_type || '本人')) {
          updateData.owner_type = accountSettings.ownerType
        }
        
        if (accountSettings.description !== (selectedAccount.value.description || '')) {
          updateData.description = accountSettings.description
        }
        
        // 更新隐藏状态
        const isHidden = !accountSettings.isVisible
        if (isHidden !== (selectedAccount.value.is_hidden || false)) {
          await api.toggleAccountHidden(selectedAccount.value.id, isHidden)
        }

        // 更新净资产排除状态
        const excludeFromNetWorth = !accountSettings.includeInNetWorth
        if (excludeFromNetWorth !== (selectedAccount.value.exclude_from_net_worth || false)) {
          await api.toggleAccountExcludeNetWorth(selectedAccount.value.id, excludeFromNetWorth)
        }
        
        // 如果有其他字段需要更新
        if (Object.keys(updateData).length > 0) {
          await api.updateAccount(selectedAccount.value.id, updateData)
        }
        
        // 刷新账户列表
        await loadAllAccounts()

        // 关闭弹窗
        accountSettingsDialogVisible.value = false
        accountDetailDialogVisible.value = false
        
        ElMessage.success('账户设置保存成功')
        
      } catch (error) {
        console.error('保存账户设置失败:', error)
        ElMessage.error('保存失败，请重试')
      } finally {
        savingSettings.value = false
      }
    }

    const resetTransactionQuery = () => {
      Object.assign(transactionQuery, {
        start_date: null,
        end_date: null,
        transaction_type: null,
        page: 1,
        page_size: 20
      })
      transactionDateRange.value = []
      queryTransactions()
    }

    const handleTransactionSizeChange = (size) => {
      transactionQuery.page_size = size
      transactionQuery.page = 1
      queryTransactions()
    }

    const handleTransactionCurrentChange = (page) => {
      transactionQuery.page = page
      queryTransactions()
    }

    // 添加账户相关
    const addAccountDialogVisible = ref(false)
    const savingAccount = ref(false)
    const accountCategoryConfig = ref({})
    const banks = ref([])
    const newAccount = reactive({
      account_name: '',
      account_type: '银行卡',
      account_subcategory: '',
      bank_id: null,
      card_type: '',
      balance: 0,
      owner_type: '本人',
      description: ''
    })

    const accountTypes = [
      { label: '现金', value: '现金' },
      { label: '银行卡', value: '银行卡' },
      { label: '电子钱包', value: '电子钱包' },
      { label: '投资账户', value: '投资账户' },
      { label: '借贷账户', value: '借贷账户' },
      { label: '其他', value: '其他' }
    ]

    const ownerTypes = [
      { label: '本人', value: '本人' },
      { label: '配偶', value: '配偶' },
      { label: '共同', value: '共同' },
      { label: '其他', value: '其他' }
    ]

    // 获取当前选择账户类型的二级分类选项
    const currentSubcategories = computed(() => {
      return accountCategoryConfig.value[newAccount.account_type] || []
    })

    const showAddAccountDialog = async () => {
      // 重置表单
      Object.assign(newAccount, {
        account_name: '',
        account_type: '银行卡',
        account_subcategory: '',
        bank_id: null,
        card_type: '',
        balance: 0,
        owner_type: '本人',
        description: ''
      })
      
      // 加载配置数据
      try {
        const [categoryConfig, bankList] = await Promise.all([
          api.getAccountCategoryConfig(),
          api.getBanks()
        ])
        accountCategoryConfig.value = categoryConfig
        banks.value = bankList
      } catch (error) {
        console.error('加载配置失败:', error)
      }
      
      addAccountDialogVisible.value = true
    }

    const saveNewAccount = async () => {
      // 验证
      if (!newAccount.account_name) {
        ElMessage.warning('请输入账户名称')
        return
      }
      
      if (!newAccount.account_type) {
        ElMessage.warning('请选择账户类型')
        return
      }

      savingAccount.value = true
      try {
        await api.createAccount(newAccount)
        ElMessage.success('账户创建成功')
        addAccountDialogVisible.value = false
        
        // 刷新账户列表
        await loadAllAccounts()
      } catch (error) {
        console.error('创建账户失败:', error)
        ElMessage.error('创建失败，请重试')
      } finally {
        savingAccount.value = false
      }
    }

    // 全局设置对话框
    const globalSettingsDialogVisible = ref(false)
    
    const showSettingsDialog = () => {
      globalSettingsDialogVisible.value = true
    }

    const editAccounts = () => {
      console.log('编辑账户')
    }

    // 工具方法
    const formatAmount = (amount) => {
      if (!showAmount.value) return '****'
      return parseFloat(amount).toLocaleString('zh-CN', { minimumFractionDigits: 2 })
    }

    const formatDateTime = (dateTime) => {
      return dayjs(dateTime).format('YYYY-MM-DD HH:mm')
    }

    const getTransactionTypeTag = (type) => {
      const tagMap = {
        '支出': 'danger',
        '收入': 'success',
        '转账': 'info',
        '借入': 'warning',
        '借出': 'warning',
        '收款': 'success',
        '还款': 'info'
      }
      return tagMap[type] || ''
    }

    // 生命周期
    onMounted(async () => {
      await loadAllAccounts()
    })
    
    // 一次请求加载全部账户，拆分为可见账户（存 store）和隐藏账户
    const loadAllAccounts = async () => {
      try {
        const allAccounts = await api.getAccounts({ include_hidden: true })
        const activeVisible = allAccounts.filter(a => !a.is_hidden && a.is_active !== false)
        store.commit('SET_ACCOUNTS', activeVisible)
        store.commit('SET_ALL_ACCOUNTS', allAccounts)
        hiddenAccounts.value = allAccounts.filter(a => a.is_hidden)
      } catch (error) {
        console.error('加载账户失败:', error)
      }
    }
    
    // 计算隐藏账户总额
    const getHiddenTotal = () => {
      return hiddenAccounts.value.reduce((sum, account) => sum + parseFloat(account.balance || 0), 0)
    }

    // 隐藏账户按一级类型分组
    const hiddenByType = computed(() => {
      const map = {}
      hiddenAccounts.value.forEach(a => {
        const t = a.account_type || '其他'
        if (!map[t]) map[t] = []
        map[t].push(a)
      })
      return map
    })

    const sortedHiddenTypes = computed(() => {
      return Object.keys(hiddenByType.value).sort((a, b) => {
        const oa = accountTypeConfig[a]?.order || 999
        const ob = accountTypeConfig[b]?.order || 999
        return oa - ob
      })
    })

    const getHiddenByType = (type) => hiddenByType.value[type] || []

    const getHiddenTypeTotal = (type) =>
      getHiddenByType(type).reduce((sum, a) => sum + parseFloat(a.balance || 0), 0)

    const getHiddenSubcategories = (type) => {
      const set = new Set(getHiddenByType(type).map(a => a.account_subcategory || ''))
      return [...set].sort((a, b) => {
        if (!a) return 1
        if (!b) return -1
        return a.localeCompare(b, 'zh')
      })
    }

    const getHiddenByTypeAndSubcat = (type, subcat) =>
      getHiddenByType(type).filter(a => (a.account_subcategory || '') === subcat)

    const getHiddenSubcatTotal = (type, subcat) =>
      getHiddenByTypeAndSubcat(type, subcat).reduce((sum, a) => sum + parseFloat(a.balance || 0), 0)
    
    // 恢复账户显示
    const restoreAccount = async (account) => {
      try {
        await api.toggleAccountHidden(account.id, false)
        ElMessage.success('账户已恢复显示')
        // 刷新数据
        await loadAllAccounts()
      } catch (error) {
        console.error('恢复账户失败:', error)
        ElMessage.error('操作失败')
      }
    }

    // 快速隐藏账户
    const quickHideAccount = async (account) => {
      try {
        await api.toggleAccountHidden(account.id, true)
        ElMessage.success(`「${account.account_name}」已隐藏`)
        await loadAllAccounts()
      } catch (error) {
        console.error('隐藏账户失败:', error)
        ElMessage.error('操作失败')
      }
    }

    // ===================== 排序模式 =====================
    const sortMode = ref(false)
    const savingOrder = ref(false)
    const localAccountsByType = ref({})
    let dragType = null
    let dragIndex = null
    const dragOverType = ref(null)
    const dragOverIndex = ref(null)

    const getSortableAccounts = (type) => localAccountsByType.value[type] || []

    const getSortableBySubcat = (type, subcat) =>
      (localAccountsByType.value[type] || []).filter(
        a => (a.account_subcategory || '') === (subcat || '')
      )

    const enterSortMode = () => {
      const copy = {}
      for (const [type, accs] of Object.entries(accountsByType.value)) {
        copy[type] = [...accs]
      }
      localAccountsByType.value = copy
      sortedAccountTypes.value.forEach(t => { expandedCategories[t] = true })
      sortMode.value = true
    }

    const exitSortMode = () => {
      sortMode.value = false
      localAccountsByType.value = {}
      dragType = null
      dragIndex = null
      dragOverType.value = null
      dragOverIndex.value = null
    }

    const onDragStart = (key, index, event) => {
      dragType = key
      dragIndex = index
      event.dataTransfer.effectAllowed = 'move'
    }

    const onDragOver = (key, index) => {
      if (dragType !== key) return
      dragOverType.value = key
      dragOverIndex.value = index
    }

    const onDrop = (key, dropIndex) => {
      if (dragType !== key || dragIndex === null || dragIndex === dropIndex) return

      if (key.includes('::')) {
        // 有子分类：在子分类内部排序
        const sepIdx = key.indexOf('::')
        const type = key.slice(0, sepIdx)
        const subcat = key.slice(sepIdx + 2)
        const allItems = localAccountsByType.value[type]
        // 找出该子分类的账户，在其中重排
        const subcatItems = allItems.filter(a => (a.account_subcategory || '') === subcat)
        const [moved] = subcatItems.splice(dragIndex, 1)
        subcatItems.splice(dropIndex, 0, moved)
        // 重建整个 type 列表（保持其他子分类顺序不变）
        const subcats = getSubcategories(type)
        const newList = []
        for (const sc of subcats) {
          if ((sc || '') === subcat) {
            newList.push(...subcatItems)
          } else {
            newList.push(...allItems.filter(a => (a.account_subcategory || '') === (sc || '')))
          }
        }
        localAccountsByType.value[type] = newList
      } else {
        // 无子分类：直接在列表内移动
        const items = localAccountsByType.value[key]
        const [removed] = items.splice(dragIndex, 1)
        items.splice(dropIndex, 0, removed)
      }

      dragIndex = dropIndex
      dragOverType.value = null
      dragOverIndex.value = null
    }

    const onDragEnd = () => {
      dragOverType.value = null
      dragOverIndex.value = null
    }

    const saveOrder = async () => {
      try {
        savingOrder.value = true
        const orders = []
        let sortOrder = 1
        for (const type of sortedAccountTypes.value) {
          const accs = localAccountsByType.value[type] || accountsByType.value[type] || []
          for (const acc of accs) {
            orders.push({ id: acc.id, sort_order: sortOrder++ })
          }
        }
        await api.reorderAccounts(orders)
        ElMessage.success('排序保存成功')
        sortMode.value = false
        localAccountsByType.value = {}
        await loadAllAccounts()
      } catch (error) {
        console.error('保存排序失败:', error)
        ElMessage.error('保存排序失败')
      } finally {
        savingOrder.value = false
      }
    }
    // ===================== 排序模式结束 =====================

    // 快速记账分类
    const quickTransactionCategories = computed(() => {
      if (quickTransaction.type === 'expense') {
        return expenseCategories.value
      } else if (quickTransaction.type === 'income') {
        return incomeCategories.value
      }
      return []
    })

    // 加载分类数据
    const loadCategoriesForQuickTransaction = async () => {
      try {
        const [expenseData, incomeData] = await Promise.all([
          api.getExpenseCategoriesTree(),
          api.getIncomeCategoriesTree()
        ])
        expenseCategories.value = expenseData
        incomeCategories.value = incomeData
      } catch (error) {
        console.error('加载分类失败:', error)
      }
    }

    // 为当前账户添加交易记录
    const addTransactionForAccount = async () => {
      if (selectedAccount.value) {
        // 重置表单
        quickTransaction.type = 'expense'
        quickTransaction.amount = ''
        quickTransaction.category = []
        quickTransaction.toAccountId = null
        quickTransaction.date = dayjs().format('YYYY-MM-DD HH:mm:ss')
        quickTransaction.description = ''
        
        // 加载分类
        if (expenseCategories.value.length === 0) {
          await loadCategoriesForQuickTransaction()
        }
        
        // 打开弹窗
        addTransactionDialogVisible.value = true
      }
    }

    // 保存快速记账
    const saveQuickTransaction = async () => {
      if (!quickTransaction.amount || parseFloat(quickTransaction.amount) <= 0) {
        ElMessage.warning('请输入有效金额')
        return
      }

      if (['expense', 'income'].includes(quickTransaction.type) && quickTransaction.category.length === 0) {
        ElMessage.warning('请选择分类')
        return
      }

      if (['transfer', 'borrow_out', 'repay'].includes(quickTransaction.type) && !quickTransaction.toAccountId) {
        ElMessage.warning('请选择转入账户')
        return
      }

      savingTransaction.value = true
      try {
        const typeMap = { expense: '支出', income: '收入', transfer: '转账', borrow_out: '借出', repay: '还款' }
        
        const requestData = {
          transaction_type: typeMap[quickTransaction.type],
          amount: parseFloat(quickTransaction.amount),
          primary_account_id: selectedAccount.value.id,
          transaction_date: quickTransaction.date,
          description: quickTransaction.description
        }

        if (quickTransaction.type === 'expense') {
          requestData.expense_major_category_id = quickTransaction.category[0]
          if (quickTransaction.category[1]) {
            requestData.expense_minor_category_id = quickTransaction.category[1]
          }
        } else if (quickTransaction.type === 'income') {
          requestData.income_major_category_id = quickTransaction.category[0]
        } else if (['transfer', 'borrow_out', 'repay'].includes(quickTransaction.type)) {
          requestData.secondary_account_id = quickTransaction.toAccountId
        }

        await api.createTransaction(requestData)
        ElMessage.success('记账成功')
        
        // 关闭弹窗
        addTransactionDialogVisible.value = false
        
        // 刷新账户数据
        await loadAllAccounts()
        await loadAccountMonthlyStats()
        
      } catch (error) {
        console.error('记账失败:', error)
        ElMessage.error('记账失败')
      } finally {
        savingTransaction.value = false
      }
    }

    return {
      accounts,
      accountsByType,
      sortedAccountTypes,
      getAccountsByType,
      getSubcategories,
      getAccountsByTypeAndSubcat,
      getSubcategoryTotal,
      expandedSubcategories,
      isSubcategoryExpanded,
      toggleSubcategory,
      getTypeConfig,
      cashAccounts,
      bankCardAccounts,
      eWalletAccounts,
      investmentAccounts,
      loanAccounts,
      otherAccounts,
      hiddenAccounts,
      totalAssets,
      totalLiabilities,
      netWorth,
      showAmount,
      expandedCategories,
      balanceDialogVisible,
      transactionsDialogVisible,
      accountDetailDialogVisible,
      accountSettingsDialogVisible,
      selectedAccount,
      balanceLoading,
      transactionsLoading,
      monthlyStatsLoading,
      loadingMore,
      savingSettings,
      balanceResult,
      accountTransactions,
      allMonthlyData,
      transactionTotal,
      transactionDateRange,
      currentYear,
      currentMonth,
      hasMoreData,
      timelineContainer,
      sortedMonthlyData,
      totalFlowIn,
      totalFlowOut,
      balanceQuery,
      transactionQuery,
      accountSettings,
      toggleAmountVisibility,
      toggleAccountsExpanded,
      toggleCategory,
      getCategoryTotal,
      getHiddenTotal,
      sortedHiddenTypes,
      getHiddenByType,
      getHiddenTypeTotal,
      getHiddenSubcategories,
      getHiddenByTypeAndSubcat,
      getHiddenSubcatTotal,
      viewAccountDetail,
      queryBalance,
      loadAccountMonthlyStats,
      loadMoreMonthlyData,
      handleScroll,
      viewMonthDetails,
      viewTransactions,
      queryTransactions,
      resetTransactionQuery,
      handleTransactionSizeChange,
      handleTransactionCurrentChange,
      showAddAccountDialog,
      showSettingsDialog,
      editAccounts,
      formatAmount,
      formatDateTime,
      getTransactionTypeTag,
      showAccountSettings,
      saveAccountSettings,
      handleScrollThrottled,
      deleteTransaction,
      editTransaction,
      handleTransactionSaved,
      deletingTransactionId,
      editTransactionDialogVisible,
      editingTransaction,
      restoreAccount,
      quickHideAccount,
      addTransactionForAccount,
      addTransactionDialogVisible,
      savingTransaction,
      quickTransactionTabs,
      quickTransaction,
      quickTransactionCategories,
      saveQuickTransaction,
      // 新增
      addAccountDialogVisible,
      savingAccount,
      newAccount,
      accountTypes,
      ownerTypes,
      accountCategoryConfig,
      currentSubcategories,
      banks,
      saveNewAccount,
      globalSettingsDialogVisible,
      currentEditSubcategories,
      onAccountTypeChange,
      // 排序模式
      sortMode,
      savingOrder,
      dragOverType,
      dragOverIndex,
      getSortableAccounts,
      getSortableBySubcat,
      enterSortMode,
      exitSortMode,
      onDragStart,
      onDragOver,
      onDrop,
      onDragEnd,
      saveOrder
    }
  }
}
</script>

<style scoped>
.accounts-page {
  height: 100%;
  background-color: #f5f7fa;
  padding: 0;
  margin: -20px;
}

/* 净资产概览卡片 */
.net-worth-card {
  background: linear-gradient(135deg, #ff7b54 0%, #ffb347 50%, #ffd93d 100%);
  border-radius: 16px;
  padding: 20px;
  margin: 20px;
  color: white;
  box-shadow: 0 8px 24px rgba(255, 123, 84, 0.3);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.actions {
  display: flex;
  gap: 8px;
}

.actions .el-button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
}

.actions .el-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

.net-worth-content .main-amount {
  display: flex;
  align-items: baseline;
  margin-bottom: 10px;
}

.amount-sign {
  font-size: 24px;
  margin-right: 4px;
}

.amount-number {
  font-size: 32px;
  font-weight: bold;
  margin-right: 8px;
}

.sub-amounts {
  display: flex;
  gap: 20px;
  font-size: 14px;
  opacity: 0.9;
}

/* 账户分类区域 */
.accounts-section {
  background: white;
  margin: 0 20px 20px 20px;
  border-radius: 12px;
  overflow: hidden;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.section-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.section-actions {
  display: flex;
  gap: 8px;
}

/* 排序模式 */
.sortable-item {
  cursor: grab;
  user-select: none;
  padding-left: 16px !important;
}
.sortable-item:active {
  cursor: grabbing;
}
.sortable-item .account-info {
  flex: 1;
}
.drag-handle {
  display: flex;
  align-items: center;
  color: #bbb;
  font-size: 16px;
  margin-right: 10px;
  flex-shrink: 0;
}
.drag-over {
  background-color: #e8f4ff;
  border-left: 3px solid #409eff;
}

/* 账户分类 */
.account-category {
  border-bottom: 1px solid #f0f0f0;
}

.account-category:last-child {
  border-bottom: none;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.category-header:hover {
  background-color: #f8f9fa;
}

.category-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.category-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: white;
}

.cash-icon {
  background: linear-gradient(135deg, #4CAF50, #8BC34A);
}

.bank-card-icon {
  background: linear-gradient(135deg, #2196F3, #03A9F4);
}

.ewallet-icon {
  background: linear-gradient(135deg, #FF9800, #FFC107);
}

.investment-icon {
  background: linear-gradient(135deg, #9C27B0, #E91E63);
}

.loan-icon {
  background: linear-gradient(135deg, #FF5722, #FF9800);
}

.other-icon {
  background: linear-gradient(135deg, #607D8B, #9E9E9E);
}

.reimbursement-icon {
  background: linear-gradient(135deg, #795548, #A1887F);
}

.category-details {
  display: flex;
  flex-direction: column;
}

.category-name {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.account-count {
  font-size: 12px;
  color: #999;
}

.category-amount {
  display: flex;
  align-items: center;
  gap: 8px;
}

.category-amount .amount {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.expand-icon {
  font-size: 12px;
  color: #999;
  transition: transform 0.2s;
}

.expand-icon.expanded {
  transform: rotate(90deg);
}

/* 账户列表 */
.account-list {
  background-color: #fafbfc;
}

.account-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px 12px 68px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.account-item:hover {
  background-color: #f0f4f8;
}

.account-item:last-child {
  border-bottom: none;
}

.account-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.account-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.account-details {
  display: flex;
  flex-direction: column;
}

.account-name {
  font-size: 14px;
  color: #333;
}

.account-type {
  font-size: 12px;
  color: #999;
}

.exclude-net-worth-tag {
  display: inline-block;
  font-size: 10px;
  color: #e6a23c;
  border: 1px solid #f0c78a;
  border-radius: 3px;
  padding: 0 4px;
  line-height: 16px;
  vertical-align: middle;
}

.account-balance {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.account-balance.negative {
  color: #f56c6c;
}

/* 账户右侧区域（余额 + 快捷隐藏按钮） */
.account-item-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.quick-hide-btn {
  opacity: 0;
  transition: opacity 0.2s;
  color: #bbb;
  border-color: #ddd;
  background: transparent;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
}

.account-item:hover .quick-hide-btn {
  opacity: 1;
}

/* 触摸设备始终显示（无 hover） */
@media (hover: none) {
  .quick-hide-btn {
    opacity: 1;
  }
}

/* 隐藏账户内的一级类型分组标题 */
.hidden-type-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 20px 8px 20px;
  background: #f2f4f7;
  border-bottom: 1px solid #e8e8e8;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s;
}

.hidden-type-header:hover {
  background: #eaecf0;
}

.hidden-type-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hidden-type-name {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.hidden-type-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hidden-type-header .expand-icon {
  font-size: 11px;
  color: #bbb;
  transition: transform 0.2s;
}

.hidden-type-header .expand-icon.expanded {
  transform: rotate(90deg);
}

/* 二级分类分组标题 */
.subcategory-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 20px 6px 56px;
  background: #f8f9fa;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s;
}

.subcategory-header:hover {
  background: #f0f2f5;
}

.subcategory-name {
  font-size: 12px;
  color: #888;
  font-weight: 500;
}

.subcategory-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.subcategory-amount {
  font-size: 12px;
  color: #aaa;
}

.subcategory-header .expand-icon {
  font-size: 11px;
  color: #bbb;
  transition: transform 0.2s;
}

.subcategory-header .expand-icon.expanded {
  transform: rotate(90deg);
}

.account-item-indented {
  padding-left: 72px;
}

/* 特殊分类 */
.special-category .category-header {
  cursor: default;
}

.special-category .category-header:hover {
  background-color: transparent;
}

/* 隐藏账户分类 */
.hidden-category {
  margin-top: 16px;
  border-top: 1px dashed #e4e7ed;
  padding-top: 16px;
}

.hidden-icon {
  background: linear-gradient(135deg, #909399 0%, #606266 100%);
}

.hidden-account-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.hidden-account-item .account-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.hidden-account-item .account-details {
  display: flex;
  flex-direction: column;
}

.hidden-account-item .account-type {
  font-size: 12px;
  color: #909399;
}

.hidden-account-item .account-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.hidden-account-item .account-balance {
  color: #909399;
}

/* 对话框样式 */
.balance-result {
  margin-top: 20px;
}

.result-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.result-item .label {
  width: 100px;
  color: #606266;
}

.balance-value {
  font-size: 18px;
  font-weight: bold;
  color: #409eff;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.amount-positive {
  color: #67c23a;
  font-weight: bold;
}

.amount-negative {
  color: #f56c6c;
  font-weight: bold;
}

/* 响应式设计 - 全面适配不同设备 */

/* 大屏幕 - 桌面端 (>1200px) */
@media (min-width: 1201px) {
  .accounts-page {
    max-width: 1200px;
    margin: -20px auto;
    padding: 0 40px;
  }
  
  .net-worth-card {
    margin: 20px 0;
    padding: 24px;
  }
  
  .accounts-section {
    margin: 20px 0;
  }
  
  .amount-number {
    font-size: 36px;
  }
  
  .sub-amounts {
    gap: 30px;
    font-size: 15px;
  }
  
  .category-header {
    padding: 18px 24px;
  }
  
  .account-item {
    padding: 14px 24px 14px 76px;
  }
  
  .category-name {
    font-size: 17px;
  }
  
  .account-name {
    font-size: 15px;
  }
  
  .account-balance {
    font-size: 15px;
  }
}

/* 中等屏幕 - 平板横屏 (769px - 1200px) */
@media (min-width: 769px) and (max-width: 1200px) {
  .accounts-page {
    margin: -20px;
  }
  
  .net-worth-card {
    margin: 20px;
    padding: 22px;
  }
  
  .accounts-section {
    margin: 0 20px 20px 20px;
  }
  
  .amount-number {
    font-size: 34px;
  }
  
  .sub-amounts {
    gap: 25px;
    font-size: 14px;
  }
  
  .category-header {
    padding: 16px 20px;
  }
  
  .account-item {
    padding: 12px 20px 12px 68px;
  }
}

/* 小屏幕 - 平板竖屏 (481px - 768px) */
@media (min-width: 481px) and (max-width: 768px) {
  .accounts-page {
    margin: -20px -15px 0 -15px;
  }
  
  .net-worth-card {
    margin: 15px;
    padding: 18px;
    border-radius: 14px;
  }
  
  .accounts-section {
    margin: 0 15px 15px 15px;
    border-radius: 10px;
  }
  
  .amount-number {
    font-size: 30px;
  }
  
  .sub-amounts {
    gap: 20px;
    font-size: 13px;
  }
  
  .category-header {
    padding: 15px 18px;
  }
  
  .account-item {
    padding: 11px 18px 11px 66px;
  }
  
  .category-icon {
    width: 34px;
    height: 34px;
  }
  
  .account-name {
    font-size: 13px;
  }
  
  .account-balance {
    font-size: 13px;
  }
}

/* 超小屏幕 - 手机 (≤480px) */
@media (max-width: 480px) {
  .accounts-page {
    margin: -20px -10px 0 -10px;
  }
  
  .net-worth-card {
    margin: 12px;
    padding: 16px;
    border-radius: 12px;
  }
  
  .card-header h3 {
    font-size: 16px;
  }
  
  .actions .el-button {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }
  
  .amount-sign {
    font-size: 20px;
  }
  
  .amount-number {
    font-size: 26px;
  }
  
  .sub-amounts {
    flex-direction: column;
    gap: 5px;
    font-size: 12px;
  }
  
  .accounts-section {
    margin: 0 12px 12px 12px;
    border-radius: 8px;
  }
  
  .section-header {
    padding: 14px 16px;
  }
  
  .section-header h4 {
    font-size: 15px;
  }
  
  .category-header {
    padding: 14px 16px;
  }
  
  .category-icon {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }
  
  .category-name {
    font-size: 14px;
  }
  
  .account-count {
    font-size: 11px;
  }
  
  .category-amount .amount {
    font-size: 14px;
  }
  
  .account-item {
    padding: 10px 16px 10px 60px;
  }
  
  .account-icon {
    width: 18px;
    height: 18px;
  }
  
  .account-name {
    font-size: 13px;
  }
  
  .account-type {
    font-size: 11px;
  }
  
  .account-balance {
    font-size: 13px;
  }
  
  .account-details {
    gap: 2px;
  }
}

/* 超窄屏幕适配 (≤320px) */
@media (max-width: 320px) {
  .net-worth-card {
    margin: 10px;
    padding: 14px;
  }
  
  .amount-number {
    font-size: 24px;
  }
  
  .sub-amounts {
    font-size: 11px;
  }
  
  .accounts-section {
    margin: 0 10px 10px 10px;
  }
  
  .category-header {
    padding: 12px 14px;
  }
  
  .category-icon {
    width: 30px;
    height: 30px;
  }
  
  .account-item {
    padding: 9px 14px 9px 56px;
  }
  
  .category-name {
    font-size: 13px;
  }
  
  .account-name {
    font-size: 12px;
  }
  
  .account-balance {
    font-size: 12px;
  }
}

/* 横屏模式适配 */
@media (orientation: landscape) and (max-height: 500px) {
  .net-worth-card {
    padding: 12px 16px;
  }
  
  .amount-number {
    font-size: 24px;
  }
  
  .sub-amounts {
    gap: 15px;
    font-size: 12px;
  }
  
  .category-header {
    padding: 10px 16px;
  }
  
  .account-item {
    padding: 8px 16px 8px 60px;
  }
}

/* 对话框响应式适配 */
@media (max-width: 768px) {
  .el-dialog {
    width: 95% !important;
    margin: 5vh auto !important;
  }
  
  .el-dialog__header {
    padding: 15px 20px 10px;
  }
  
  .el-dialog__body {
    padding: 10px 20px 20px;
  }
  
  .el-form-item__label {
    font-size: 14px;
  }
  
  .el-table {
    font-size: 12px;
  }
  
  .el-table th {
    padding: 8px 0;
  }
  
  .el-table td {
    padding: 10px 0;
  }
}

@media (max-width: 480px) {
  .el-dialog {
    width: 98% !important;
    margin: 2vh auto !important;
  }
  
  .el-form--inline .el-form-item {
    display: block;
    margin-right: 0;
    margin-bottom: 10px;
  }
  
  .el-date-editor {
    width: 100% !important;
  }
  
  .el-table {
    font-size: 11px;
  }
  
  .el-pagination {
    text-align: center;
  }
  
  .el-pagination .el-pager li {
    min-width: 24px;
    height: 24px;
    line-height: 24px;
    font-size: 12px;
  }
  
  .el-pagination .btn-prev,
  .el-pagination .btn-next {
    min-width: 24px;
    height: 24px;
    line-height: 24px;
  }
}


/* 触摸设备优化 */
@media (hover: none) and (pointer: coarse) {
  .category-header,
  .account-item {
    padding: 16px 20px;
    min-height: 48px;
  }
  
  .account-item {
    padding-left: 72px;
  }
  
  .actions .el-button {
    min-width: 44px;
    min-height: 44px;
  }
}

/* 高分辨率显示器适配 */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .category-icon,
  .account-icon {
    backface-visibility: hidden;
    transform: translateZ(0);
  }
}

/* 深色模式适配 */
@media (prefers-color-scheme: dark) {
  .accounts-page {
    background-color: #1a1a1a;
  }
  
  .accounts-section {
    background: #2d2d2d;
    border: 1px solid #404040;
  }
  
  .category-header:hover {
    background-color: #363636;
  }
  
  .account-list {
    background-color: #262626;
  }
  
  .account-item:hover {
    background-color: #363636;
  }
  
  .category-name,
  .account-name,
  .account-balance {
    color: #ffffff;
  }
  
  .account-count,
  .account-type {
    color: #a0a0a0;
  }
}

/* 减少动画效果 - 无障碍访问 */
@media (prefers-reduced-motion: reduce) {
  .category-header,
  .account-item,
  .expand-icon {
    transition: none;
  }
  
  .account-card {
    transform: none;
  }
}

/* 账户详情弹窗样式 */
.account-overview-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.account-overview-card {
  border-radius: 12px;
  padding: 20px;
  color: white;
  margin-bottom: 20px;
}

.account-overview-card.positive-balance {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.account-overview-card.negative-balance {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.account-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.account-basic-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.account-title {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
}

.account-type-tag {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 8px;
  border-radius: 4px;
}

.balance-section {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.balance-label {
  font-size: 14px;
  color: white;
  font-weight: 500;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-value.positive {
  color: #67c23a;
}

.summary-value.negative {
  color: #f56c6c;
}

.settings-icon {
  font-size: 16px;
  cursor: pointer;
  opacity: 0.7;
  margin-left: 8px;
}

.settings-icon:hover {
  opacity: 1;
}

.summary-flow {
  display: flex;
  gap: 20px;
  font-size: 14px;
  color: white;
  font-weight: 500;
}

.account-actions-bar {
  margin-top: 16px;
}

.account-actions-bar .el-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
}

.account-actions-bar .el-button:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

/* 快速记账弹窗样式 */
.quick-transaction-form {
  padding: 0 10px;
}

.type-tabs {
  display: flex;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 4px;
  margin-bottom: 20px;
}

.type-tab {
  flex: 1;
  text-align: center;
  padding: 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: all 0.2s;
}

.type-tab.active {
  background: #409eff;
  color: white;
}

.type-tab:hover:not(.active) {
  background: #e9e9e9;
}

.amount-input-section {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 8px;
}

.currency-symbol {
  font-size: 24px;
  color: #333;
  margin-right: 8px;
}

.amount-input :deep(.el-input__wrapper) {
  box-shadow: none;
  background: transparent;
}

.amount-input :deep(.el-input__inner) {
  font-size: 32px;
  font-weight: bold;
  color: #333;
}

.form-field {
  margin-bottom: 16px;
}

.form-field label {
  display: block;
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
}

/* 月度统计图表区域 */
.chart-area {
  margin-bottom: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
}

.chart-months {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 8px;
}

.month-item {
  background: white;
  border-radius: 8px;
  padding: 12px 8px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e0e0e0;
}

.month-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.month-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
}

.month-flow {
  font-size: 10px;
  margin-bottom: 6px;
}

.month-flow .flow-in {
  color: #67c23a;
  display: block;
}

.month-flow .flow-out {
  color: #f56c6c;
  display: block;
}

.month-balance {
  font-size: 14px;
  font-weight: 600;
}

.month-balance.positive {
  color: #67c23a;
}

.month-balance.negative {
  color: #f56c6c;
}

.balance-label {
  font-size: 10px;
  color: #999;
  display: block;
  font-weight: normal;
}

/* 月度明细列表 */
.monthly-details {
  max-height: 300px;
  overflow-y: auto;
}

.month-detail-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.month-detail-item:hover {
  background: #f8f9fa;
  border-color: #409eff;
}

.month-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.month-name {
  font-weight: 600;
  color: #303133;
}

.transaction-count {
  font-size: 12px;
  color: #909399;
  background: #f0f0f0;
  padding: 2px 8px;
  border-radius: 12px;
}

.month-amounts {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.amount-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.amount-item .label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.amount-item .value {
  font-size: 14px;
  font-weight: 600;
}

.amount-item .value.positive {
  color: #67c23a;
}

.amount-item .value.negative {
  color: #f56c6c;
}

/* 时间线容器样式 */
.timeline-section {
  background: #fafafa;
  border-radius: 8px;
  padding: 16px;
  margin-top: 20px;
}

.timeline-container {
  height: 400px; /* 固定高度 */
  overflow-y: auto;
  padding: 0 16px;
  box-sizing: border-box;
  background: #fafafa;
  border-radius: 8px;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding: 16px 0 10px 0;
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
  position: sticky;
  top: 0;
  z-index: 10;
}

.timeline-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.subtitle {
  font-size: 12px;
  color: #999;
}

.load-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}

.load-tip {
  font-size: 12px;
  color: #999;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.section-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.timeline-content {
  position: relative;
}

.timeline-item {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  margin-bottom: 8px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #f0f0f0;
}

.timeline-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
  border-color: #409eff;
}

.timeline-date {
  width: 60px;
  text-align: center;
  margin-right: 16px;
  flex-shrink: 0;
}

.timeline-date .year {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.timeline-date .month {
  font-size: 16px;
  color: #333;
  font-weight: 600;
  margin-top: 2px;
}

.timeline-content-item {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.month-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.flow-summary {
  display: flex;
  gap: 24px;
  align-items: center;
}

.flow-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.flow-item .label {
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
}

.flow-item.flow-in .amount {
  font-size: 14px;
  font-weight: 600;
  color: #67c23a;
}

.flow-item.flow-out .amount {
  font-size: 14px;
  font-weight: 600;
  color: #f56c6c;
}

.balance-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.balance-amount {
  font-size: 16px;
  font-weight: 700;
}

.balance-amount.positive {
  color: #67c23a;
}

.balance-amount.negative {
  color: #f56c6c;
}

.balance-label {
  font-size: 10px;
  color: #999;
  font-weight: normal;
}

.transaction-info {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #999;
}

.transaction-count {
  font-weight: 600;
  color: #409eff;
  background: #e1f3ff;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
}

.arrow-icon {
  font-size: 12px;
  color: #999;
}

.transaction-types {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
  border-top: 1px solid #f5f5f5;
  padding-top: 8px;
}

.type-tag {
  background-color: #f0f0f0;
  color: #666;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
}

/* 加载更多指示器 */
.load-more-indicator {
  text-align: center;
  padding: 10px 0;
  color: #999;
  font-size: 14px;
}

.auto-load-tip {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #999;
}

.loading-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #999;
}

/* 没有更多数据 */
.no-more-data {
  text-align: center;
  padding: 10px 0;
  color: #999;
  font-size: 14px;
}

/* 无数据状态 */
.no-data {
  text-align: center;
  padding: 40px 20px;
}

.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.no-data-icon {
  font-size: 48px;
  color: #d0d0d0;
}

.no-data p {
  margin: 0;
  font-size: 16px;
  color: #666;
  font-weight: 500;
}

.no-data-tip {
  font-size: 12px;
  color: #999;
}

/* 全局设置弹窗样式 */
.global-settings-content {
  min-height: 300px;
}

.settings-section {
  padding: 20px;
}

.settings-section h4 {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.settings-tip {
  margin: 8px 0;
  font-size: 14px;
  color: #666;
  line-height: 1.6;
}

.form-tip {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}
</style>

<style>
/* 账户设置弹窗手机适配 - 全局样式（dialog 经 teleport 渲染到 body，scoped 无效） */
@media (max-width: 768px) {
  .account-settings-dialog .el-dialog__body {
    padding: 10px 16px 16px;
  }

  /* 标签改为顶部对齐 */
  .account-settings-dialog .el-form-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 16px;
  }

  .account-settings-dialog .el-form-item__label {
    width: auto !important;
    text-align: left !important;
    padding-bottom: 4px;
    line-height: 1.4;
  }

  .account-settings-dialog .el-form-item__content {
    margin-left: 0 !important;
    width: 100%;
  }

  /* switch 行：标签左、开关右，横向排列 */
  .account-settings-dialog .el-form-item:has(.el-switch) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    flex-wrap: nowrap;
    gap: 8px;
  }

  .account-settings-dialog .el-form-item:has(.el-switch) .el-form-item__label {
    padding-bottom: 0;
    flex-shrink: 0;
  }

  .account-settings-dialog .el-form-item:has(.el-switch) .el-form-item__content {
    width: auto;
    flex-shrink: 0;
    margin-left: 0 !important;
  }

  .account-settings-dialog .el-form-item:has(.el-switch) .form-tip {
    display: none;
  }

  /* 底部按钮全宽堆叠 */
  .account-settings-dialog .el-dialog__footer {
    padding: 12px 16px;
  }

  .account-settings-dialog .dialog-footer {
    display: flex;
    flex-direction: column-reverse;
    gap: 8px;
    width: 100%;
  }

  .account-settings-dialog .dialog-footer .el-button {
    width: 100%;
    margin-left: 0 !important;
  }
}
</style> 
